// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The tower as a STRUCTURE: what is where, what may be built, and which storeys the lifts actually
// reach. Nothing here moves — `sim.ts` owns everything that does.
//
// ── ONE RULE SHAPES THE WHOLE GAME ──────────────────────────────────────────────────────────────
// A riser occupies two columns on EVERY storey it passes. There are twelve columns. So the lift you
// need costs you the floors you wanted, on every floor at once, permanently — and a second riser for
// redundancy costs a sixth of the entire tower. That is the trade the genre is built on, and it is
// the reason this is a management game rather than a placement game.
//
// ── AND ONE SIMPLIFICATION EARNS ITS KEEP ───────────────────────────────────────────────────────
// Every riser starts at the concourse. Nobody builds a lift that does not reach the front door, and
// the payoff is that routing has no general graph search in it: either one riser serves both ends of
// a journey, or the journey is two legs with a change at the concourse, and there is never a third
// case. An earlier design spent a day on Dijkstra with EWMA edge weights and cache invalidation to
// answer a question this constraint answers for free.

import {
  CONCOURSE,
  COLS,
  RISER_COST_PER_STOREY,
  RISER_MIN_SPAN,
  RISER_UPKEEP_PER_STOREY,
  MAX_RISERS,
  RISER_W,
  STAIR_COST,
  STOREYS,
  UNITS,
  type UnitKind,
} from './tuning';

export type RiserPolicy = 'all' | 'express' | 'down';

export interface Unit {
  id: number;
  kind: UnitKind;
  storey: number;
  col: number;
  w: number;
  /** Whether anybody is actually in it. An empty unit costs upkeep and earns nothing. */
  let: boolean;
  sat: number;
  pop: number;
  vacantDays: number;
  /** Set when satisfaction has been in the danger band two nights running. Gone the next night. */
  notice: boolean;
  footfallToday: number;
  /** Trips this unit's people completed today, and how many of those were bad. For the unit card. */
  tripsToday: number;
  lateTripsToday: number;
}

export interface Riser {
  id: number;
  /** Always CONCOURSE. Kept as a field so the renderer and the audit never assume it. */
  lo: number;
  hi: number;
  col: number;
  policy: RiserPolicy;
  /** With policy 'express', the riser serves the concourse and storeys at or above this. */
  expressFrom: number;
}

export interface Stair {
  id: number;
  /** Links `storey` and `storey + 1`. */
  storey: number;
  col: number;
}

export const CELL_EMPTY = 0;
export const CELL_UNIT = 1;
export const CELL_RISER = 2;
export const CELL_STAIR = 3;

export interface Tower {
  storeys: number;
  units: Unit[];
  risers: Riser[];
  stairs: Stair[];
  /** What kind of thing occupies each cell, and its id. Indexed `storey * COLS + col`. */
  kind: Uint8Array;
  id: Int16Array;
  nextUnitId: number;
  nextRiserId: number;
  nextStairId: number;
}

export const idx = (storey: number, col: number): number => storey * COLS + col;

export function createTower(): Tower {
  return {
    storeys: 1,
    units: [],
    risers: [],
    stairs: [],
    kind: new Uint8Array(STOREYS * COLS),
    id: new Int16Array(STOREYS * COLS).fill(-1),
    nextUnitId: 0,
    nextRiserId: 0,
    nextStairId: 0,
  };
}

export interface Verdict {
  ok: boolean;
  why: string;
}
const YES: Verdict = { ok: true, why: '' };
const no = (why: string): Verdict => ({ ok: false, why });

function spanFree(t: Tower, storey: number, col: number, w: number): boolean {
  if (col < 0 || col + w > COLS) return false;
  for (let c = col; c < col + w; c++) if (t.kind[idx(storey, c)] !== CELL_EMPTY) return false;
  return true;
}

export function canPlaceUnit(t: Tower, kind: UnitKind, storey: number, col: number, maxStorey: number): Verdict {
  const spec = UNITS[kind];
  if (storey < 0 || storey >= STOREYS) return no('Off the tower');
  if (storey > maxStorey) return no(`Storey ${storey} is above your permit`);
  if (col + spec.w > COLS) return no('Not enough width there');
  if (!spanFree(t, storey, col, spec.w)) return no('Something is already there');
  return YES;
}

export function placeUnit(t: Tower, kind: UnitKind, storey: number, col: number): Unit {
  const spec = UNITS[kind];
  const u: Unit = {
    id: t.nextUnitId++,
    kind,
    storey,
    col,
    w: spec.w,
    let: false,
    sat: 0,
    pop: 0,
    vacantDays: 0,
    notice: false,
    footfallToday: 0,
    tripsToday: 0,
    lateTripsToday: 0,
  };
  t.units.push(u);
  for (let c = col; c < col + spec.w; c++) {
    t.kind[idx(storey, c)] = CELL_UNIT;
    t.id[idx(storey, c)] = u.id;
  }
  t.storeys = Math.max(t.storeys, storey + 1);
  return u;
}

export function canPlaceRiser(t: Tower, hi: number, col: number, maxStorey: number): Verdict {
  if (t.risers.length >= MAX_RISERS) return no(`${MAX_RISERS} risers is the most this lot will carry`);
  if (hi < RISER_MIN_SPAN) return no(`A riser has to reach storey ${RISER_MIN_SPAN} at least`);
  if (hi >= STOREYS) return no('Off the top of the tower');
  if (hi > maxStorey) return no('Above your permit');
  if (col + RISER_W > COLS) return no('Not enough width there');
  for (let s = CONCOURSE; s <= hi; s++) {
    if (!spanFree(t, s, col, RISER_W)) return no(`Storey ${s} is in the way`);
  }
  return YES;
}

export function placeRiser(t: Tower, hi: number, col: number): Riser {
  const r: Riser = { id: t.nextRiserId++, lo: CONCOURSE, hi, col, policy: 'all', expressFrom: 9 };
  t.risers.push(r);
  for (let s = CONCOURSE; s <= hi; s++) {
    for (let c = col; c < col + RISER_W; c++) {
      t.kind[idx(s, c)] = CELL_RISER;
      t.id[idx(s, c)] = r.id;
    }
  }
  t.storeys = Math.max(t.storeys, hi + 1);
  return r;
}

/** Extending a riser upward is the Refit lever: it is cheap per storey and it is the only way to
 *  reach the top of a tower that is already full. */
export function canExtendRiser(t: Tower, r: Riser, hi: number, maxStorey: number): Verdict {
  if (hi <= r.hi) return no('That is not higher than it already goes');
  if (hi >= STOREYS) return no('Off the top of the tower');
  if (hi > maxStorey) return no('Above your permit');
  for (let s = r.hi + 1; s <= hi; s++) {
    if (!spanFree(t, s, r.col, RISER_W)) return no(`Storey ${s} is in the way`);
  }
  return YES;
}

export function extendRiser(t: Tower, r: Riser, hi: number): void {
  for (let s = r.hi + 1; s <= hi; s++) {
    for (let c = r.col; c < r.col + RISER_W; c++) {
      t.kind[idx(s, c)] = CELL_RISER;
      t.id[idx(s, c)] = r.id;
    }
  }
  r.hi = hi;
  t.storeys = Math.max(t.storeys, hi + 1);
}

export function canPlaceStair(t: Tower, storey: number, col: number, maxStorey: number): Verdict {
  if (storey < 0 || storey + 1 >= STOREYS) return no('Off the tower');
  if (storey + 1 > maxStorey) return no('Above your permit');
  if (!spanFree(t, storey, col, 1) || !spanFree(t, storey + 1, col, 1)) return no('Something is in the way');
  return YES;
}

export function placeStair(t: Tower, storey: number, col: number): Stair {
  const s: Stair = { id: t.nextStairId++, storey, col };
  t.stairs.push(s);
  for (const st of [storey, storey + 1]) {
    t.kind[idx(st, col)] = CELL_STAIR;
    t.id[idx(st, col)] = s.id;
  }
  t.storeys = Math.max(t.storeys, storey + 2);
  return s;
}

export function removeUnit(t: Tower, u: Unit): void {
  for (let c = u.col; c < u.col + u.w; c++) {
    t.kind[idx(u.storey, c)] = CELL_EMPTY;
    t.id[idx(u.storey, c)] = -1;
  }
  const i = t.units.indexOf(u);
  if (i >= 0) t.units.splice(i, 1);
}

export const unitAt = (t: Tower, storey: number, col: number): Unit | null => {
  if (storey < 0 || storey >= STOREYS || col < 0 || col >= COLS) return null;
  if (t.kind[idx(storey, col)] !== CELL_UNIT) return null;
  const id = t.id[idx(storey, col)];
  return t.units.find((u) => u.id === id) ?? null;
};

export const riserAt = (t: Tower, storey: number, col: number): Riser | null => {
  if (storey < 0 || storey >= STOREYS || col < 0 || col >= COLS) return null;
  if (t.kind[idx(storey, col)] !== CELL_RISER) return null;
  const id = t.id[idx(storey, col)];
  return t.risers.find((r) => r.id === id) ?? null;
};

/** Whether a riser's cars will open their doors on a storey at all. */
export function serves(r: Riser, storey: number): boolean {
  if (storey < r.lo || storey > r.hi) return false;
  if (r.policy === 'express' && storey !== CONCOURSE && storey < r.expressFrom) return false;
  return true;
}

/** Whether stairs connect these two adjacent storeys. */
export function stairBetween(t: Tower, a: number, b: number): boolean {
  const lo = Math.min(a, b);
  return Math.abs(a - b) === 1 && t.stairs.some((s) => s.storey === lo);
}

/** Where a person standing on `storey` boards riser `r` — the middle of its two columns. */
export const boardingCol = (r: Riser): number => r.col + (RISER_W - 1) / 2;

export const riserCost = (hi: number): number => (hi - CONCOURSE + 1) * RISER_COST_PER_STOREY;
export const riserUpkeep = (r: Riser): number => (r.hi - r.lo + 1) * RISER_UPKEEP_PER_STOREY;
export const extendCost = (r: Riser, hi: number): number => (hi - r.hi) * RISER_COST_PER_STOREY;
export const stairCost = (): number => STAIR_COST;

/**
 * Every storey any riser opens its doors on. This is what leasing consults, and it is deliberately a
 * property of the STRUCTURE rather than of any journey: a storey with a riser but no car is served on
 * paper and hopeless in practice, and the game should let a player build exactly that mistake and
 * then watch it fail, rather than refusing the placement and never explaining why.
 */
export function servedStoreys(t: Tower): boolean[] {
  const out = new Array<boolean>(STOREYS).fill(false);
  for (const r of t.risers) for (let s = r.lo; s <= r.hi; s++) if (serves(r, s)) out[s] = true;
  // Stairs extend service one storey at a time, which is exactly as useful as it sounds.
  for (let pass = 0; pass < 2; pass++) {
    for (const st of t.stairs) {
      if (out[st.storey]) out[st.storey + 1] = true;
      if (out[st.storey + 1]) out[st.storey] = true;
    }
  }
  return out;
}
